// utils/sanitize.js
import DOMPurify from 'dompurify';

// Constants for limits
export const SECURITY_CONFIG = {
  MAX_FILES_IN_ZIP: 10000,
  MAX_COMPRESSION_RATIO: 100,
  MAX_EXTRACTED_SIZE: 500 * 1024 * 1024, // 500MB
  MAX_FILE_SIZE_INDIVIDUAL: 50 * 1024 * 1024, // 50MB
  MAX_TOTAL_FILES: 5000,
  MAX_FILENAME_LENGTH: 255,
  MAX_CODE_LENGTH: 10 * 1024 * 1024, // 10MB per file
    ALLOWED_EXTENSIONS: [
    '.py', '.js', '.ts', '.html', '.css', '.scss', '.vue', '.java', 
    '.c', '.cpp', '.cs', '.go', '.php', '.rb', '.json', '.yaml', 
    '.yml', '.xml', '.md', '.txt', '.sh', 'Dockerfile', 'Makefile',
    '.gitignore', '.7z', '.zip', '.tar.gz'  // Include archive extensions
  ] 
};

// Sanitize filenames against path traversal - FIXED: removed control chars regex
export function sanitizeFilename(filename) {
  if (!filename) return '';
  
  let safe = filename
    .replace(/\.\.\//g, '')  // Remove ../
    .replace(/\.\.\\/g, '')   // Remove ..\ (Windows)
    .replace(/^\/+/, '')      // Remove leading slashes
    .replace(/^\\+/, '')      // Remove leading backslashes
    .replace(/[<>"|?*]/g, '_') // Replace Windows forbidden chars
    .substring(0, SECURITY_CONFIG.MAX_FILENAME_LENGTH);
  
  // Prevent path traversal via encoded sequences
  safe = safe.replace(/%2e%2e%2f/gi, '')
             .replace(/%2e%2e%5c/gi, '')
             .replace(/%2e%2e\//gi, '')
             .replace(/\.\.%2f/gi, '');
  
  return safe;
}

// Escape HTML entities for safe display
export function escapeHtml(text) {
  if (!text) return '';
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
    .replace(/\//g, '&#x2F;')
    .replace(/`/g, '&#96;');
}

// Sanitize code for preview (with DOMPurify)
export function sanitizeCode(code) {
  if (!code) return '';
  
  // Truncate extremely long code
  if (code.length > SECURITY_CONFIG.MAX_CODE_LENGTH) {
    code = code.substring(0, SECURITY_CONFIG.MAX_CODE_LENGTH) + '... [truncated]';
  }
  
  // First escape HTML, then let DOMPurify clean it
  const escaped = escapeHtml(code);
  return DOMPurify.sanitize(escaped, {
    ALLOWED_TAGS: [], // No HTML tags allowed
    ALLOWED_ATTR: []  // No attributes allowed
  });
}

// Sanitize any user input for display
export function sanitizeForDisplay(text) {
  if (!text) return '';
  
  return escapeHtml(text)
    .replace(/javascript:/gi, '')
    .replace(/on\w+=/gi, '')
    .replace(/data:/gi, '');
}

// Validate file type by extension
export function isValidFileType(filename) {
  if (!filename) return false;
  
  const ext = filename.substring(filename.lastIndexOf('.')).toLowerCase();
  return SECURITY_CONFIG.ALLOWED_EXTENSIONS.includes(ext) ||
         filename.includes('Dockerfile') ||
         filename.includes('Makefile') ||
         filename.includes('.gitignore');
}

// Check for suspicious filename patterns
export function isSuspiciousFilename(filename) {
  if (!filename) return false;
  
  const suspicious = [
    /\.\./,           // Path traversal
    /^~/,             // Home directory
    /^\//,            // Absolute path
    /^[A-Z]:\\/i,     // Windows drive letter
    /^\\\\/,          // Windows UNC path
    /%2e%2e/i,        // URL encoded path traversal
    /;/g,             // Command injection
    /&/g,             // Command injection
    /\|/g,            // Command injection
    /\$/g,            // Variable injection
    /`/g,             // Command substitution
    /\(/g,            // Shell injection
    /\)/g             // Shell injection
  ];
  
  return suspicious.some(pattern => pattern.test(filename));
}

// Safe JSON stringify with circular reference protection
export function safeStringify(obj, spaces = 2) {
  const seen = new WeakSet();
  return JSON.stringify(obj, (key, value) => {
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) {
        return '[Circular]';
      }
      seen.add(value);
    }
    return value;
  }, spaces);
}